<template>
    <div class="questionnaire-container">
        <div class="component-wrapper">
            <component :is="steps[index]"
                v-for="(step,index) in steps"
                v-show="index === currentStepIndex"
                ref="components"
                :key="index"
                :isActive="index === currentStepIndex"
                @completed="handleStepCompleted"
                @in-progress="handleStepInProgress"
            />
        </div>
        <div class="buttons-wrapper">
            <button
                v-show="currentStepIndex > 0"
                :class="['btn', 'widget__button', { 'btn--disabled': isButtonDisabled }]"
                @click="handleCancelQuestionnaire"
            >
                {{ $t('global.button.cancel') }}
            </button>
            <button
                :class="['btn', 'widget__button', { 'btn--disabled': isButtonDisabled }]"
                :disabled="isButtonDisabled"
                @click="proceedToNextStep"
            >
                {{ currentStepButtonText }}
            </button>
        </div>
    </div>
</template>

<script>
/* eslint-disable */

// 3rd party Libs
import {
    mapGetters,
    mapActions,
} from 'vuex';

// Shared Modules
import sharedStoreTypes from '@shared/store/types.js';
import sharedEpicTypes from '@shared/epics/types.js';

// Local Modules
import localStoreTypes from '@webapp/store/types.js';

// Components
import startQuestionnaire from '@webapp/modules/customer-first/components/aml-questionnaire/content/start-questionnaire.vue';
import question1 from '@webapp/modules/customer-first/components/aml-questionnaire/content/question-1.vue';
import question2 from '@webapp/modules/customer-first/components/aml-questionnaire/content/question-2.vue';
import question3 from '@webapp/modules/customer-first/components/aml-questionnaire/content/question-3.vue';
import question4 from '@webapp/modules/customer-first/components/aml-questionnaire/content/question-4.vue';
import question5 from '@webapp/modules/customer-first/components/aml-questionnaire/content/question-5.vue';

export default {
    name: 'aml-questionnaire',
    components: {
        startQuestionnaire,
        question1,
        question2,
        question3,
        question4,
        question5,
    },
    props: {
        cancelRoute: {
            default: null,
            type: Object,
        },
        refreshUserDetails: {
            default: false,
            type: Boolean,
        },
        redirecTo: {
            default: null,
            type: Object,
        }
    },
    data() {
        return {
            steps: ['start-questionnaire', 'question-1', 'question-2', 'question-3', 'question-4', 'question-5'],
            currentStepIndex: 0,
            currentStepCompleted: false,
            questionnaireCompleted: false,
            questionnaireData: {},
            onModalClose: null,
        };
    },
    computed: {
        ...mapGetters([
            'userAMLQuestionnaire',
            'getLatestSimpleValidationError',
        ]),

        isFirstStep() {
            return this.steps[this.currentStepIndex] === 'start_questionnaire';
        },

        currentStepButtonText() {
            if (this.currentStepIndex === 0) {
                return this.$t('userAMLQuestionnaire.actions.start');
            }

            if (this.currentStepIndex === this.steps.length - 1) {
                return this.$t('global.button.submit');
            }

            return this.$t('userAMLQuestionnaire.actions.nextStep');
        },

        isButtonDisabled() {
            return this.currentStepCompleted === false;
        }
    },
    watch: {
        getLatestSimpleValidationError(error) {
            const fetchErrors = this.utilGlobalHandleErrorMessages(error);

            this.$notify({
                type: 'error',
                text: fetchErrors.message,
            });
        },
    },
    methods: {
        ...mapActions({
            submitQuestionnaire: sharedStoreTypes.SET_USER_AML_QUESTIONNAIRE,
            setModal: localStoreTypes.SET_MODAL,
            getUserDetails: sharedEpicTypes.GET_USER_DETAILS,
        }),

        proceedToNextStep() {
            if (this.currentStepIndex === (this.steps.length - 1)) {
                this.handleSubmitQuestionnaire();

                return;
            }

            this.currentStepIndex++;

            if (this.questionnaireCompleted) {
                this.handleStepCompleted({});
            } else {
                this.handleStepInProgress();
            }
        },
        handleStepCompleted(stepData) {
            this.currentStepCompleted = true;

            Object.assign(this.questionnaireData, stepData);
        },
        handleStepInProgress() {
            this.currentStepCompleted = false;
        },
        handleSubmitQuestionnaire() {
            this.setModal({
                isVisible: true,
                isClosable: false,
                componentToLoad: 'modules/customer-first/components/aml-questionnaire/confirmation-modal',
                data: {
                    image: require('@webapp/assets/warning.webp'),
                    title: this.$t("userAMLQuestionnaire.confirmModalTitle"),
                    message: this.$t("userAMLQuestionnaire.confirmModalDescription"),
                    submit: {
                        buttonText: this.$t('global.button.submit'),
                        callback: async () => {
                            await this.submitQuestionnaire(this.questionnaireData);

                            if (this.refreshUserDetails) {
                                await this.getUserDetails();
                            }

                            if (this.redirecTo) {
                                this.$router.push(this.redirecTo);
                            }

                            this.$notify({
                                type: 'success',
                                text: this.$t('global.saved'),
                            });
                        },
                    },
                    cancel: {
                        buttonText: this.$t('userAMLQuestionnaire.actions.reviewMyAnswers'),
                        callback: () => {
                            this.currentStepIndex = 1;
                            this.questionnaireCompleted = true;
                        },
                    }
                },
            });
        },
        handleCancelQuestionnaire() {
            this.setModal({
                isVisible: true,
                isClosable: false,
                componentToLoad: 'modules/customer-first/components/aml-questionnaire/confirmation-modal',
                data: {
                    image: require('@webapp/assets/oopps.webp'),
                    title: this.$t("userAMLQuestionnaire.cancelModalTitle"),
                    message: this.$t("userAMLQuestionnaire.cancelModalDescription"),
                    submit: {
                        buttonText: this.$t('global.button.confirm'),
                        callback: () => {
                            if (this.cancelRoute) {
                                this.$router.push(this.cancelRoute);

                                return;
                            }
                            this.$refs.components.forEach((component) => component.resetStep?.());

                            this.currentStepIndex = 0;
                            this.currentStepCompleted = false;
                            this.questionnaireCompleted = false;
                            this.questionnaireData = {};
                        },
                    },
                    cancel: {
                        buttonText: this.$t('global.button.cancel'),
                    }
                },
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.questionnaire-container {
    max-width: 1024px;
    padding: 16px;
    margin: auto;
    color: $black-color;
    background-color: $white-color;
    border-radius: 8px;

    .buttons-wrapper {
        display: flex;
        justify-content: flex-end;

        .btn {
            border: 0.2rem solid $lime-green-color;

            @include min-width(md) {
                width: 21.9rem;
            }

            &:first-child {
                background: none;
                color: $lime-green-color;
                opacity: 0.7;
            }

            &:last-child {
                margin-left: 3rem;
                background: $lime-green-color;
                color: $daintree-color;
            }

            &--disabled {
                opacity: 0.3;
            }
        }
    }
}
</style>